"use client";

/**
 * Admin Auth Context Provider
 *
 * 관리자 인증 상태를 관리한다. AuthProvider와 동일한 DI 패턴으로 API 클라이언트를 생성한다.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useRouter } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";

import { createApiClient, type ApiClient } from "@/shared/api/client";
import { createAuthService } from "@/shared/api/services/authService";
import { removeToken, setToken } from "@/shared/auth/cookie";
import type { AdminAuthState, AdminUser } from "@/shared/auth/types";
import { API_BASE_URL } from "@/config/env";

export interface AdminAuthContextValue extends AdminAuthState {
  adminLogin: (email: string, password: string) => Promise<void>;
  adminLogout: () => void;
  apiClient: ApiClient;
}

const AdminAuthContext = createContext<AdminAuthContextValue | null>(null);

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const queryClient = useQueryClient();

  const [admin, setAdmin] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const getTokenSync = useCallback((): string | null => {
    if (typeof document === "undefined") return null;
    const match = document.cookie
      .split(";")
      .map((c) => c.trim())
      .find((c) => c.startsWith("admin_token="));
    if (!match) return null;
    const raw = decodeURIComponent(match.slice("admin_token=".length));
    return raw.startsWith("Bearer ") ? raw.slice(7) : raw;
  }, []);

  const handleUnauthorized = useCallback(
    (returnUrl: string) => {
      router.push("/admin/login?returnUrl=" + encodeURIComponent(returnUrl));
    },
    [router],
  );

  const apiClient = useMemo(
    () =>
      createApiClient({
        baseUrl: API_BASE_URL,
        getToken: getTokenSync,
        onUnauthorized: handleUnauthorized,
      }),
    [getTokenSync, handleUnauthorized],
  );

  const authService = useMemo(() => createAuthService(apiClient), [apiClient]);

  // 마운트 시 레거시 중복 쿠키 정리 + 관리자 정보 복원
  useEffect(() => {
    const token = getTokenSync();
    if (token) {
      removeToken("admin");
      setToken("admin", token);
    }
    if (!token) {
      setIsLoading(false);
      return;
    }

    authService
      .getAdminInfo()
      .then((res) => {
        // Sequelize raw spread 대응: top-level 또는 dataValues 중 존재하는 쪽을 사용
        const raw = res.data;
        const id = raw.id ?? raw.dataValues?.id ?? null;
        const name = raw.name ?? raw.dataValues?.name ?? "";
        const role = raw.role ?? "";
        const isSuperAdmin = role === "SUPER_ADMIN";
        setAdmin({ id, name, email: "", role, isSuperAdmin });
      })
      .catch(() => {
        removeToken("admin");
        setAdmin(null);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [authService, getTokenSync]);

  const adminLogin = useCallback(
    async (email: string, password: string) => {
      setIsLoading(true);
      try {
        const res = await authService.adminLogin({ email, memberPw: password });
        const { token, name: loginName } = res.data;
        queryClient.removeQueries();
        setToken("admin", token);

        // 로그인 응답엔 role 정보가 없으므로 /brands/info 를 이어서 호출해
        // isSuperAdmin 을 도출한다. 실패 시에도 기본 관리자로 계속 진입.
        try {
          const infoRes = await authService.getAdminInfo();
          const raw = infoRes.data;
          const id = raw.id ?? raw.dataValues?.id ?? null;
          const name = raw.name ?? raw.dataValues?.name ?? loginName;
          const role = raw.role ?? "";
          const isSuperAdmin = role === "SUPER_ADMIN";
          setAdmin({ id, name, email, role, isSuperAdmin });
        } catch {
          setAdmin({ id: null, name: loginName, email, role: "admin", isSuperAdmin: false });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [authService, queryClient],
  );

  const adminLogout = useCallback(() => {
    queryClient.clear();
    removeToken("admin");
    setAdmin(null);
    window.location.href = "/admin/login";
  }, [queryClient]);

  const value = useMemo<AdminAuthContextValue>(
    () => ({
      admin,
      isAuthenticated: admin !== null,
      isLoading,
      adminLogin,
      adminLogout,
      apiClient,
    }),
    [admin, isLoading, adminLogin, adminLogout, apiClient],
  );

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

/** AdminAuthContext 직접 접근용 */
export function useAdminAuth(): AdminAuthContextValue {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) {
    throw new Error("useAdminAuth must be used within AdminAuthProvider");
  }
  return ctx;
}

/**
 * Admin apiClient 로 도메인 service 인스턴스를 메모이즈해 반환.
 * useBuyerService 의 admin 버전 — 같은 패턴 통일.
 *
 * @example
 *   const service = useAdminService(createAdminService);
 */
export function useAdminService<S>(factory: (client: ApiClient) => S): S {
  const { apiClient } = useAdminAuth();
  return useMemo(() => factory(apiClient), [factory, apiClient]);
}
